import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMFOItems } from "@/hooks/use-mfo-items";
import { MFOItem } from "@/types/kanban";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  BarChart3,
  Calendar,
  CheckCircle2,
  Clock,
  Users,
  Building2,
  FileText,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { getPriorityColor } from "@/utils/statusPriorityUtils";
import { useToast } from "@/hooks/use-toast";
import { publicSupabase } from "@/integrations/supabase/public-client";
import { createNotification } from "@/utils/notificationUtils";

interface MFODatabaseItem {
  id: string;
  code: string;
  title: string;
  priority: string;
  status: string;
  progress: number;
  due_date: string;
  budget: number;
  department_id: string;
  public_access_token: string;
  departments?: {
    name: string;
  };
}

interface SuccessIndicator {
  indicator: string;
}

interface SubTask {
  id: string;
  text: string;
  completed: boolean;
}

interface Collaborator {
  id: string;
  employee_id: string;
  employees: {
    id: string;
    name: string;
  };
}

interface Timeline {
  q1: boolean;
  q2: boolean;
  q3: boolean;
  q4: boolean;
}

interface SupabaseError {
  message: string;
  details?: string;
  hint?: string;
  code?: string;
}

interface ToastError {
  message: string;
}

const PublicMFODetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { getMFODetail, updateSubtaskCompletion } = useMFOItems();
  const [mfo, setMfo] = useState<MFOItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const { toast } = useToast();

  useEffect(() => {
    const fetchData = async () => {
      if (!id || !token) {
        toast({
          title: "Invalid Access",
          description: "This MFO is not publicly accessible",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      try {
      
        
        // First, let's check if we can access the table at all
        const { data: testData, error: testError } = await publicSupabase
          .from("mfo_items")
          .select("id")
          .limit(1);



        if (testError) {
          console.error("Error accessing mfo_items table:", testError);
          toast({
            title: "Error",
            description: "Unable to access MFO data. Please check your permissions.",
            variant: "destructive"
          });
          return;
        }

        // First verify the token exists in the database and get the MFO data
        const { data: mfoData, error: mfoError } = await publicSupabase
          .from("mfo_items")
          .select(`
            id,
            code,
            title,
            priority,
            status,
            progress,
            due_date,
            budget,
            department_id,
            public_access_token,
            departments (
              name
            )
          `)
          .eq("id", id)
          .eq("public_access_token", token)
          .maybeSingle() as { data: MFODatabaseItem | null; error: SupabaseError | null };


        if (mfoError) {
          console.error("Error fetching MFO:", mfoError);
          toast({
            title: "Error",
            description: "Failed to load MFO details",
            variant: "destructive"
          });
          return;
        }

        if (!mfoData) {
    
          // Let's try to find the MFO without the token to see if it exists
          const { data: mfoWithoutToken, error: mfoWithoutTokenError } = await publicSupabase
            .from("mfo_items")
            .select("id, public_access_token")
            .eq("id", id)
            .maybeSingle();

      

          if (mfoWithoutTokenError) {
            console.error("Error checking MFO existence:", mfoWithoutTokenError);
            toast({
              title: "Error",
              description: "Unable to verify MFO existence",
              variant: "destructive"
            });
            return;
          }

          if (mfoWithoutToken) {
         
            toast({
              title: "Invalid Access",
              description: "The access token is invalid",
              variant: "destructive"
            });
          } else {
            toast({
              title: "Invalid Access",
              description: "This MFO is not publicly accessible or does not exist",
              variant: "destructive"
            });
          }
          return;
        }

        // Get success indicators
        const { data: indicatorsData, error: indicatorsError } = await publicSupabase
          .from("mfo_success_indicators")
          .select("indicator")
          .eq("mfo_id", id) as { data: SuccessIndicator[] | null; error: SupabaseError | null };

        if (indicatorsError) {
          console.error("Error fetching success indicators:", indicatorsError);
          toast({
            title: "Error",
            description: "Failed to load success indicators",
            variant: "destructive"
          });
          return;
        }

        // Get sub-tasks
        const { data: tasksData, error: tasksError } = await publicSupabase
          .from("mfo_subtasks")
          .select("id, text, completed")
          .eq("mfo_id", id);

        if (tasksError) {
          console.error("Error fetching sub-tasks:", tasksError);
          toast({
            title: "Error",
            description: "Failed to load sub-tasks",
            variant: "destructive"
          });
          return;
        }

        // Get collaborators
        const { data: collaboratorsData, error: collaboratorsError } = await publicSupabase
          .from("mfo_assignees")
          .select(`
            id,
            employee_id,
            employees (
              id,
              name
            )
          `)
          .eq("mfo_id", id) as { data: Collaborator[] | null; error: SupabaseError | null };

        if (collaboratorsError) {
          console.error("Error fetching collaborators:", collaboratorsError);
          toast({
            title: "Error",
            description: "Failed to load team members",
            variant: "destructive"
          });
          return;
        }

        // Transform the data to match the MFOItem type
        const transformedMfo: MFOItem = {
          id: mfoData.id,
          code: mfoData.code,
          title: mfoData.title,
          priority: mfoData.priority,
          status: mfoData.status,
          progress: mfoData.progress,
          dueDate: mfoData.due_date,
          budget: mfoData.budget,
          departmentId: mfoData.department_id,
          departmentName: mfoData.departments?.name || null,
          successIndicators: indicatorsData?.map(i => i.indicator) || [],
          subTasks: tasksData?.map(t => ({
            id: t.id,
            text: t.text,
            completed: t.completed
          })) || [],
          collaborators: (collaboratorsData || [])
            .map(c => ({
              id: c.employee_id,
              name: c.employees.name
            })),
          publicAccessToken: mfoData.public_access_token,
          timeline: {
            q1: false,
            q2: false,
            q3: false,
            q4: false
          }
        };

        setMfo(transformedMfo);
      } catch (error) {
        console.error("Error fetching MFO detail:", error);
        toast({
          title: "Error",
          description: "Failed to load MFO details",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    // Set up real-time subscription for subtask changes
    if (id) {
      const subtaskSubscription = publicSupabase
        .channel(`mfo_subtasks:${id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'mfo_subtasks',
            filter: `mfo_id=eq.${id}`
          },
          async (payload) => {
            // Recalculate progress when subtasks change
            const { data: subtasks } = await publicSupabase
              .from('mfo_subtasks')
              .select('completed')
              .eq('mfo_id', id);

            if (subtasks) {
              const totalSubtasks = subtasks.length;
              const completedSubtasks = subtasks.filter(task => task.completed).length;
              const newProgress = Math.round((completedSubtasks / totalSubtasks) * 100);

              // Update local state
              setMfo(prev => {
                if (!prev) return null;
                return {
                  ...prev,
                  progress: newProgress
                };
              });
            }
          }
        )
        .subscribe();

      return () => {
        subtaskSubscription.unsubscribe();
      };
    }
  }, [id, token, toast]);


  const handleSubtaskToggle = async (subtaskId: string, completed: boolean) => {
    try {
      // Update the subtask completion status
      const { error: updateError } = await publicSupabase
        .from("mfo_subtasks")
        .update({ completed })
        .eq("id", subtaskId);

      if (updateError) {
        console.error("Error updating subtask:", updateError);
        toast({
          title: "Error",
          description: "Failed to update subtask status",
          variant: "destructive"
        });
        return;
      }

      // Get all subtasks for this MFO
      const { data: subtasks, error: subtasksError } = await publicSupabase
        .from("mfo_subtasks")
        .select("completed")
        .eq("mfo_id", id);

      if (subtasksError) {
        console.error("Error fetching subtasks:", subtasksError);
        return;
      }

      // Calculate new progress
      const totalSubtasks = subtasks.length;
      const completedSubtasks = subtasks.filter(task => task.completed).length;
      const newProgress = Math.round((completedSubtasks / totalSubtasks) * 100);

      // Update MFO progress
      const { error: progressError } = await publicSupabase
        .from("mfo_items")
        .update({ progress: newProgress })
        .eq("id", id);

      if (progressError) {
        console.error("Error updating MFO progress:", progressError);
        return;
      }

      // Create a notification for the subtask completion
      if (mfo) {
        // Get the MFO's collaborators to notify them
        const { data: collaborators, error: collaboratorsError } = await publicSupabase
          .from("mfo_assignees")
          .select("employee_id")
          .eq("mfo_id", id);

        if (!collaboratorsError && collaborators) {
          // Create notifications for each collaborator
          const notifications = collaborators.map(collaborator => ({
            user_id: collaborator.employee_id,
            title: "Subtask Completed",
            message: `An individual accountable have successfully complete a sub-task in ${mfo.code}: ${mfo.title}`,
            type: "subtask_completed",
            is_read: false
          }));

          if (notifications.length > 0) {
            const { error: notificationError } = await publicSupabase
              .from("notifications")
              .insert(notifications);

            if (notificationError) {
              console.error("Error creating notifications:", notificationError);
            }
          }
        }
      }

      // Update local state
      setMfo((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          subTasks: prev.subTasks.map((task) =>
            task.id === subtaskId ? { ...task, completed } : task
          ),
          progress: newProgress
        };
      });
    } catch (err) {
      const error = err as SupabaseError;
      toast({
        title: "Error",
        description: error.message || "Failed to update subtask status",
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-xl">Loading...</p>
      </div>
    );
  }

  if (!mfo) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <p className="text-xl mb-4">MFO not found or not publicly accessible</p>
        <Button onClick={() => window.location.href = "/"}>
          Return to Home
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">

      <div className="flex flex-col lg:flex-row justify-between items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold mb-1">
            {mfo.code}: {mfo.title}
          </h1>
          <p className="text-muted-foreground">
            {mfo.departmentName || "Department not specified"}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Priority
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Badge
              className={`text-xs font-semibold uppercase ${getPriorityColor(
                mfo.priority
              )}`}
            >
              {mfo.priority}
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Status
            </CardTitle>
          </CardHeader>
          <CardContent className="flex items-center">
            {mfo.status === "completed" && (
              <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
            )}
            {mfo.status === "in-progress" && (
              <Clock className="h-4 w-4 mr-2 text-blue-500" />
            )}
            {mfo.status === "not-started" && (
              <Clock className="h-4 w-4 mr-2 text-gray-500" />
            )}
            {mfo.status === "delayed" && (
              <Clock className="h-4 w-4 mr-2 text-red-500" />
            )}
            <span
              className={`capitalize ${
                mfo.status === "completed"
                  ? "text-green-700"
                  : mfo.status === "in-progress"
                  ? "text-blue-700"
                  : mfo.status === "delayed"
                  ? "text-red-700"
                  : "text-gray-700"
              }`}
            >
              {mfo.status.replace("-", " ")}
            </span>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Due Date
            </CardTitle>
          </CardHeader>
          <CardContent className="flex items-center">
            <Calendar className="h-4 w-4 mr-2" />
            <span>
              {new Date(mfo.dueDate).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </CardContent>
        </Card>
      </div>

      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-2">Progress</h2>
        <div className="flex items-center">
          <Progress value={mfo.progress} className="h-2 flex-1 mr-4" />
          <span className="text-sm font-medium">{mfo.progress}%</span>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="overview">
            <BarChart3 className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="tasks">
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Tasks
          </TabsTrigger>
          <TabsTrigger value="team">
            <Users className="h-4 w-4 mr-2" />
            Team
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Success Indicators</CardTitle>
              <CardDescription>
                Key performance indicators for this MFO
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {mfo.successIndicators?.length ? (
                mfo.successIndicators.map((indicator, index) => (
                  <div
                    key={index}
                    className="p-3 border rounded-lg bg-accent/10"
                  >
                    {indicator}
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground">
                  No success indicators defined
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Budget</CardTitle>
              <CardDescription>
                Allocated budget for this MFO item
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                ₱{mfo.budget.toLocaleString()}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Timeline</CardTitle>
              <CardDescription>
                Timeline for implementation across quarters
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-2">
                <div
                  className={`p-3 text-center rounded-lg ${
                    mfo.timeline?.q1
                      ? "bg-primary text-primary-foreground"
                      : "bg-accent"
                  }`}
                >
                  Q1 
                </div>
                <div
                  className={`p-3 text-center rounded-lg ${
                    mfo.timeline?.q2
                      ? "bg-primary text-primary-foreground"
                      : "bg-accent"
                  }`}
                >
                  Q2
                </div>
                <div
                  className={`p-3 text-center rounded-lg ${
                    mfo.timeline?.q3
                      ? "bg-primary text-primary-foreground"
                      : "bg-accent"
                  }`}
                >
                  Q3
                </div>
                <div
                  className={`p-3 text-center rounded-lg ${
                    mfo.timeline?.q4
                      ? "bg-primary text-primary-foreground"
                    : "bg-accent"
                  }`}
                >
                  Q4
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tasks" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Sub-Tasks</CardTitle>
              <CardDescription>
                Track sub-tasks completion for this MFO
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {mfo.subTasks && mfo.subTasks.length > 0 ? (
                  mfo.subTasks.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-center p-2 border rounded-lg"
                    >
                      <input
                        type="checkbox"
                        id={task.id}
                        checked={task.completed}
                        onChange={(e) =>
                          handleSubtaskToggle(task.id, e.target.checked)
                        }
                        className="h-4 w-4 mr-3 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <label
                        htmlFor={task.id}
                        className={`flex-1 ${
                          task.completed ? "line-through text-muted-foreground" : ""
                        }`}
                      >
                        {task.text}
                      </label>
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground">No sub-tasks defined</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="team" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>INDIVIDUAL ACCOUNTABLE</CardTitle>
              <CardDescription>
                Personnel assigned to this MFO
              </CardDescription>
            </CardHeader>
            <CardContent>
              {mfo.collaborators && mfo.collaborators.length > 0 ? (
                <div className="space-y-3">
                  {mfo.collaborators.map((person) => (
                    <div
                      key={person.id}
                      className="flex items-center p-3 border rounded-lg"
                    >
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-medium mr-3">
                        {person.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </div>
                      <div>
                        <p className="font-medium">{person.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">
                  No team members assigned yet
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PublicMFODetail; 